import { useActionItem } from '@workboard/action-item';
import { useFormik, FormikErrors } from 'formik';
import { useCallback } from 'react';
import { useFieldPropsGetter } from '../form/useFieldPropsGetter';
import { useNotification } from '../notifications';
import { Repeat, TimeUnit } from '../Repeat/model';

export enum StopAfterType {
  Never,
  Some,
}
export type FormData = {
  repeat: Repeat;
  stopAfterType: StopAfterType;
  stopAfterUnit?: TimeUnit;
  stopAfterUnits?: number;
};

const msgRequired = 'Field is required';

export function useRepeatAiForm(aiId: string, initialData?: Partial<FormData>) {
  const notify = useNotification();
  const { changeRepeat, actionItem } = useActionItem(aiId);
  const postRepeat = changeRepeat.mutateAsync;

  const setRepeat = useCallback(
    async (formData: FormData) => {
      try {
        await postRepeat(formData as any);
        notify.success('Recurrence updated');
      } catch (e) {
        notify.error(
          'There was a problem while setting a repeat. Please try again later.'
        );
        throw e;
      }
    },
    [postRepeat, notify]
  );

  const aiRepeat = (actionItem?.repeat || {}) as FormData;
  const {
    submitForm,
    setFieldValue,
    errors: formErrors,
    isSubmitting,
    isValidating,
    values,
  } = useFormik<FormData>({
    initialValues: {
      repeat: aiRepeat.repeat ?? Repeat.NoRepeat,
      stopAfterType: aiRepeat.stopAfterType ?? StopAfterType.Never,
      stopAfterUnit: aiRepeat.stopAfterType === StopAfterType.Never ? TimeUnit.Days : aiRepeat.stopAfterUnit,
      stopAfterUnits: aiRepeat.stopAfterUnits ?? 0,
      ...initialData,
    },
    validate: (data) => {
      const errors: FormikErrors<FormData> = {};
      if (!Repeat[data.repeat]) {
        errors.repeat = msgRequired;
      }
      if (!StopAfterType[data.stopAfterType]) {
        errors.stopAfterType = msgRequired;
      }
      if (data.stopAfterType === StopAfterType.Some) {
        if (!TimeUnit[data.stopAfterUnit]) {
          errors.stopAfterUnit = msgRequired;
        }
        if (
          typeof data.stopAfterUnits !== 'number' ||
          Number.isNaN(data.stopAfterUnits)
        ) {
          errors.stopAfterUnits = msgRequired;
        }
      }
      return errors;
    },
    onSubmit: setRepeat,
  });
  const isProcessing = isSubmitting || isValidating;

  const getFieldProps = useFieldPropsGetter<FormData>({
    formErrors,
    isProcessing,
    setFieldValue,
    values,
  });

  return {
    values,
    getFieldProps,
    submitForm,
    isProcessing,
    formErrors,
  };
}
